import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/auth-context';

interface SubscriptionEvent {
  type: 'connected' | 'heartbeat' | 'subscription_update';
  data?: any;
  message?: string;
  timestamp: number;
}

export function useSubscriptionEvents() {
  const { user, refreshUserSubscription } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connectSSE = () => {
    if (!user?.id || typeof window === 'undefined') return;

    // 清理现有连接
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // console.log('🔗 Establishing SSE connection for subscription events...');
    
    const eventSource = new EventSource('/api/subscription-events', {
      withCredentials: true
    });

    eventSource.onopen = () => {
      // console.log('✅ SSE connection opened');
      reconnectAttempts.current = 0; // 重置重连计数
    };

    eventSource.onmessage = (event) => {
      try {
        const data: SubscriptionEvent = JSON.parse(event.data);
        
        switch (data.type) {
          case 'connected':
            // console.log('🔗 SSE connection confirmed:', data.message);
            break;
            
          case 'heartbeat':
            // 静默处理心跳，不打印日志避免噪音
            break;
            
          case 'subscription_update':
            // console.log('📡 Received subscription update via SSE:', data.data);
            
            // 立即刷新本地订阅缓存
            refreshUserSubscription().then(() => {
              console.log('✅ Local subscription cache updated from SSE event');
            }).catch((error: any) => {
              console.error('❌ Failed to refresh subscription from SSE event:', error);
            });
            break;
            
          default:
            // console.log('📨 Unknown SSE event type:', data.type);
        }
      } catch (error) {
        console.error('❌ Error parsing SSE event data:', error);
      }
    };

    eventSource.onerror = (error) => {
      // console.error('❌ SSE connection error:', error);
      eventSource.close();
      
      // 实现指数退避重连策略
      if (reconnectAttempts.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000); // 最大30秒
        reconnectAttempts.current++;
        
        // console.log(`🔄 Attempting to reconnect SSE in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          connectSSE();
        }, delay);
      } else {
        console.error('❌ Max SSE reconnection attempts reached, giving up');
      }
    };

    eventSourceRef.current = eventSource;
  };

  const disconnectSSE = () => {
    if (eventSourceRef.current) {
      // console.log('🔌 Closing SSE connection');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    reconnectAttempts.current = 0;
  };

  useEffect(() => {
    // 确保只在客户端运行
    if (typeof window === 'undefined') return;
    
    if (user?.id) {
      // 用户登录时建立SSE连接
      connectSSE();
    } else {
      // 用户登出时断开SSE连接
      disconnectSSE();
    }

    // 清理函数
    return () => {
      disconnectSSE();
    };
  }, [user?.id]);

  // 页面可见性变化时重连（用户切换标签页回来时）
  useEffect(() => {
    // 确保只在客户端运行
    if (typeof window === 'undefined') return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id) {
        // 页面变为可见且用户已登录，检查连接状态
        if (!eventSourceRef.current || eventSourceRef.current.readyState === EventSource.CLOSED) {
          // console.log('🔄 Page became visible, reconnecting SSE...');
          connectSSE();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id]);

  return {
    isConnected: typeof window !== 'undefined' && eventSourceRef.current?.readyState === EventSource.OPEN,
    reconnect: connectSSE,
    disconnect: disconnectSSE
  };
} 